import React, { useState, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Link as MuiLink,
  Divider,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import api from '../../config/api';
import OtherToolsSection from '../OtherToolsSection';

const faqs = [
  {
    q: 'What is DNS?',
    a: 'DNS (Domain Name System) is a system that translates human-readable domain names into IP addresses.'
  },
  {
    q: 'What records are checked?',
    a: 'The tool checks A, AAAA, MX, TXT, SPF, DKIM, DMARC, NS, and WHOIS records.'
  },
  {
    q: 'Why do I need a selector for DKIM?',
    a: 'DKIM selectors help identify which public key to use for email verification. Common selectors are "default" or "mail".'
  },
  {
    q: 'What is WHOIS information?',
    a: 'WHOIS provides registration and ownership details for domain names.'
  },
  {
    q: 'How long do DNS changes take?',
    a: 'DNS changes can take up to 48 hours to propagate globally.'
  },
  {
    q: 'What are A and AAAA records?',
    a: 'A records map domains to IPv4 addresses, while AAAA records map to IPv6 addresses.'
  },
  {
    q: 'What are NS records?',
    a: 'NS (Name Server) records specify which servers are authoritative for your domain.'
  },
  {
    q: 'Why check multiple record types?',
    a: 'Different record types serve different purposes in email delivery and website hosting.'
  },
  {
    q: 'What is DNS propagation?',
    a: 'DNS propagation is the time it takes for DNS changes to spread across all DNS servers worldwide.'
  },
  {
    q: 'How often should I check my DNS?',
    a: 'Regular DNS checks help ensure your domain is properly configured and secure.'
  }
];

interface DNSRecord {
  type?: string;
  value?: string | string[];
  ttl?: number;
  ip?: string;
  pri?: number;
  target?: string;
  txt?: string;
  entries?: string[];
}

interface DNSResult {
  domain: string;
  timestamp: string;
  selector?: string;
  spf?: string;
  spf_tree?: any;
  dmarc?: string;
  dkim?: {
    selector: string;
    record: string;
  };
  dns?: {
    a?: DNSRecord[];
    aaaa?: DNSRecord[];
    mx?: DNSRecord[];
    ns?: DNSRecord[];
    txt?: DNSRecord[];
    ptr?: DNSRecord[];
  };
  whois?: {
    registrar?: string;
    created?: string;
    expires?: string;
    updated?: string;
    status?: string[];
    nameservers?: string[];
    source?: string;
    age?: {
      years: number;
      months: number;
      days: number;
      total_days: number;
      formatted: string;
    };
  };
}

const DNSSuperTool: React.FC = () => {
  const [domain, setDomain] = useState('');
  const [selector, setSelector] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<DNSResult | null>(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const resultRef = useRef<HTMLDivElement>(null);

  const validateDomain = (d: string) => /^([a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/.test(d);
  const validateSelector = (s: string) => !s || /^[a-zA-Z0-9._-]+$/.test(s);

  const handleAnalyze = async () => {
    setError('');
    setResult(null);
    if (!domain) {
      setError('Please enter a domain.');
      return;
    }
    if (!validateDomain(domain)) {
      setError('Please enter a valid domain.');
      return;
    }
    if (selector && !validateSelector(selector)) {
      setError('Please enter a valid selector (letters, numbers, dash, dot, underscore).');
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams({
        domain: domain
      });
      if (selector) {
        params.append('selector', selector);
      }
      
      const response = await api.get(`/api/dns/check-all?${params.toString()}`);
      console.log('DNS Response:', response.data);
      
      // Format the response to match the expected interface
      const formattedResult: DNSResult = {
        domain: response.data.domain,
        timestamp: response.data.timestamp,
        dns: response.data.dns,
        spf: response.data.spf,
        dmarc: response.data.dmarc,
        dkim: response.data.dkim,
        whois: response.data.whois
      };
      
      setResult(formattedResult);
    } catch (err: any) {
      console.error('DNS analysis error:', err);
      if (err.response?.status === 401) {
        setError('Authentication required. Please check your API key configuration.');
      } else if (err.response?.status === 429) {
        setError('Rate limit exceeded. Please try again in a minute.');
      } else {
        setError(err.response?.data?.message || err.message || 'Failed to fetch DNS data. Please try again.');
      }
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!result) return;
    const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let y = pageHeight / 2 - 60;
    
    // Title page
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(32);
    doc.setTextColor('#0B60B1');
    doc.text('DNS AUDIT REPORT', 60, y);
    y += 40;
    doc.setFontSize(18);
    doc.setTextColor('#222');
    doc.text(`Domain: ${result.domain}`, 60, y);
    y += 28;
    doc.setFontSize(12);
    doc.setTextColor('#888');
    const now = new Date();
    doc.text(`Date: ${now.toLocaleDateString()}   Year: ${now.getFullYear()}`, 60, y);
    y += 22;
    doc.setFontSize(10);
    doc.text('Generated by: QuillBee – Deliverability Agent for ImpactQuill', 60, y);

    // Records pages
    const addRecordPage = (title: string, records: any) => {
      doc.addPage();
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      doc.setTextColor('#0B60B1');
      doc.text(title, 40, 40);
      let y = 60;
      doc.setFont('courier', 'normal');
      doc.setFontSize(11);
      doc.setTextColor('#222');
      if (Array.isArray(records)) {
        records.forEach((record: any) => {
          doc.text(`${record.type || ''} ${record.value || ''}`, 40, y);
          y += 16;
        });
      } else if (records) {
        doc.text(`${records.type || ''} ${records.value || ''}`, 40, y);
      }
    };

    if (result.dns?.a) addRecordPage('A Records', result.dns.a);
    if (result.dns?.aaaa) addRecordPage('AAAA Records', result.dns.aaaa);
    if (result.dns?.mx) addRecordPage('MX Records', result.dns.mx);
    if (result.dns?.txt) addRecordPage('TXT Records', result.dns.txt);
    if (result.spf) addRecordPage('SPF Record', result.spf);
    if (result.dkim?.record) addRecordPage('DKIM Record', result.dkim.record);
    if (result.dmarc) addRecordPage('DMARC Record', result.dmarc);
    if (result.dns?.ns) addRecordPage('NS Records', result.dns.ns);
    if (result.whois) {
      doc.addPage();
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      doc.setTextColor('#0B60B1');
      doc.text('WHOIS Information', 40, 40);
      let y = 60;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
      doc.setTextColor('#222');
      Object.entries(result.whois).forEach(([key, value]) => {
        if (value) {
          doc.text(`${key}: ${Array.isArray(value) ? value.join(', ') : value}`, 40, y);
          y += 16;
        }
      });
    }

    // Thank you page
    doc.addPage();
    doc.setFillColor(11, 96, 177);
    doc.rect(0, 0, pageWidth, pageHeight, 'F');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(28);
    doc.setTextColor('#fff');
    doc.text('Thank you for choosing ImpactQuill', pageWidth / 2, pageHeight / 2, { align: 'center' });
    
    doc.save(`${result.domain}_dns_audit_report.pdf`);
  };

  const handleDownloadJPG = async () => {
    if (!resultRef.current) return;
    const canvas = await html2canvas(resultRef.current, { backgroundColor: '#fff', scale: 2 });
    const link = document.createElement('a');
    link.download = `${domain}_dns_result.jpg`;
    link.href = canvas.toDataURL('image/jpeg', 0.95);
    link.click();
  };

  const renderRecordSection = (title: string, records: any, type: string) => {
    if (!records || records.length === 0) {
      return (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {title}
            </Typography>
            <Typography color="text.secondary">
              No {type} records found
            </Typography>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {title}
          </Typography>
          {records.map((record: any, index: number) => (
            <Box key={index} sx={{ mb: 1 }}>
              <Typography variant="body2" component="div">
                <strong>Type:</strong> {record.type}
              </Typography>
              <Typography variant="body2" component="div">
                <strong>Value:</strong> {record.value}
              </Typography>
              {record.ttl && (
                <Typography variant="body2" component="div">
                  <strong>TTL:</strong> {record.ttl} seconds
                </Typography>
              )}
              {record.pri && (
                <Typography variant="body2" component="div">
                  <strong>Priority:</strong> {record.pri}
                </Typography>
              )}
              {record.target && (
                <Typography variant="body2" component="div">
                  <strong>Target:</strong> {record.target}
                </Typography>
              )}
              <Divider sx={{ my: 1 }} />
            </Box>
          ))}
        </CardContent>
      </Card>
    );
  };

  const renderEmailRecords = () => {
    if (!result) return null;

    return (
      <Box sx={{ mt: 3 }}>
        <Typography variant="h5" gutterBottom>
          Email Configuration
        </Typography>
        
        {/* SPF Record */}
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              SPF Record
            </Typography>
            {result.spf ? (
              <Typography variant="body2" component="div">
                {result.spf}
              </Typography>
            ) : (
              <Typography color="text.secondary">
                No SPF record found
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* DMARC Record */}
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              DMARC Record
            </Typography>
            {result.dmarc ? (
              <Typography variant="body2" component="div">
                {result.dmarc}
              </Typography>
          ) : (
              <Typography color="text.secondary">
                No DMARC record found
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* DKIM Record */}
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              DKIM Record
            </Typography>
            {result.dkim ? (
              <>
                <Typography variant="body2" component="div">
                  <strong>Selector:</strong> {result.dkim.selector}
                </Typography>
                <Typography variant="body2" component="div">
                  <strong>Record:</strong> {result.dkim.record}
                </Typography>
              </>
            ) : (
              <Typography color="text.secondary">
                No DKIM record found
            </Typography>
          )}
          </CardContent>
        </Card>

        {/* MX Records */}
        {renderRecordSection('Mail Exchange (MX) Records', result.dns?.mx, 'MX')}
      </Box>
    );
  };

  // Debug: Log WHOIS data if present
  if (result && result.whois) {
    console.log('WHOIS DATA:', result.whois);
  }

  return (
    <Box sx={{ bgcolor: '#f7fafd', minHeight: '100vh', py: 0 }}>
      {/* Hero Banner */}
      <Box sx={{ bgcolor: '#0B60B1', color: '#fff', py: 6, textAlign: 'center' }}>
        <Typography variant="h3" fontWeight={700} gutterBottom>DNS Super Tool</Typography>
        <Typography variant="h6" maxWidth={600} mx="auto">
          Comprehensive DNS record analysis and validation
        </Typography>
      </Box>

      {/* Input Section */}
      <Box sx={{ py: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography variant="subtitle1" mb={2}>Enter your domain to check DNS records:</Typography>
        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, width: '100%', maxWidth: 600 }}>
          <TextField
            label="Domain"
            variant="outlined"
            value={domain}
            onChange={e => setDomain(e.target.value)}
            fullWidth
            disabled={loading}
            error={!!error}
            helperText={error}
            onKeyDown={e => {
              if (e.key === 'Enter' && !loading && domain) handleAnalyze();
            }}
          />
          <TextField
            label="DKIM Selector (Optional)"
            variant="outlined"
            value={selector}
            onChange={e => setSelector(e.target.value)}
            fullWidth
            disabled={loading}
            onKeyDown={e => {
              if (e.key === 'Enter' && !loading && domain) handleAnalyze();
            }}
          />
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleAnalyze} 
            disabled={loading || !domain} 
            sx={{ 
              minWidth: 120,
              height: 56,
              '& .MuiCircularProgress-root': {
                color: 'white'
              }
            }}
          >
            {loading ? <CircularProgress size={24} /> : 'Analyze'}
          </Button>
        </Box>
        {loading && <CircularProgress sx={{ mt: 3 }} />}
        {error && <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>}
      </Box>

      {/* Result Section */}
      {result && (
        <Card ref={resultRef} sx={{ width: '100%', maxWidth: 900, mx: 'auto', mb: 4, p: 2, boxShadow: 6, borderRadius: 3, bgcolor: '#fff' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <MuiLink href={`https://${result.domain}`} target="_blank" rel="noopener noreferrer" underline="hover" variant="subtitle1" sx={{ fontWeight: 600, fontSize: 18 }}>
                  {result.domain}
                </MuiLink>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title={copied ? 'Copied!' : 'Copy Results'}>
                  <IconButton onClick={() => { navigator.clipboard.writeText(JSON.stringify(result, null, 2)); setCopied(true); setTimeout(() => setCopied(false), 1200); }}>
                    <ContentCopyIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Download PDF">
                  <IconButton onClick={handleDownloadPDF}>
                    <PictureAsPdfIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Download as JPG">
                  <IconButton onClick={handleDownloadJPG}>
                    <ImageIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            <Divider sx={{ mb: 2 }} />

            {/* Record Sections */}
            {result.dns?.a && renderRecordSection('A Records', result.dns.a, 'A')}
            {result.dns?.aaaa && renderRecordSection('AAAA Records', result.dns.aaaa, 'AAAA')}
            {result.dns?.txt && renderRecordSection('TXT Records', result.dns.txt, 'TXT')}
            {result.dns?.ns && renderRecordSection('NS Records', result.dns.ns, 'NS')}

            {/* Email Configuration */}
            {renderEmailRecords()}

            {/* WHOIS Information */}
            {result.whois && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>WHOIS Information</Typography>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f7fafd' }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                    {result.whois.registrar && (
                      <Box>
                        <Typography variant="body2" color="text.secondary">Registrar:</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>{result.whois.registrar}</Typography>
                      </Box>
                    )}
                    {result.whois.created && (
                      <Box>
                        <Typography variant="body2" color="text.secondary">Register Date:</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {new Date(result.whois.created).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </Typography>
                      </Box>
                    )}
                    {result.whois.expires && (
                      <Box>
                        <Typography variant="body2" color="text.secondary">Expiry Date:</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {new Date(result.whois.expires).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </Typography>
                      </Box>
                    )}
                    {result.whois.updated && (
                      <Box>
                        <Typography variant="body2" color="text.secondary">Last Updated:</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {new Date(result.whois.updated).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </Typography>
                      </Box>
                    )}
                    {result.whois.age && result.whois.age.total_days > 0 && (
                      <Box>
                        <Typography variant="body2" color="text.secondary">Domain Age:</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>{result.whois.age.formatted}</Typography>
                          <Chip 
                            size="small" 
                            label={`${result.whois.age.total_days} days`}
                            sx={{ 
                              bgcolor: result.whois.age.years > 5 ? '#e8f5e9' : 
                                     result.whois.age.years > 2 ? '#fff3e0' : '#ffebee',
                              color: result.whois.age.years > 5 ? '#2e7d32' : 
                                    result.whois.age.years > 2 ? '#ef6c00' : '#c62828',
                              fontWeight: 500
                            }}
                          />
                        </Box>
                      </Box>
                    )}
                    {result.whois.status && result.whois.status.length > 0 && (
                      <Box>
                        <Typography variant="body2" color="text.secondary">Status:</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {result.whois.status.map((status, index) => (
                            <Chip
                              key={index}
                              size="small"
                              label={status}
                              sx={{
                                bgcolor: status.toLowerCase().includes('active') ? '#e8f5e9' :
                                        status.toLowerCase().includes('pending') ? '#fff3e0' :
                                        status.toLowerCase().includes('expired') ? '#ffebee' : '#e3f2fd',
                                color: status.toLowerCase().includes('active') ? '#2e7d32' :
                                      status.toLowerCase().includes('pending') ? '#ef6c00' :
                                      status.toLowerCase().includes('expired') ? '#c62828' : '#1565c0',
                                fontWeight: 500
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                    {result.whois.nameservers && result.whois.nameservers.length > 0 && (
                      <Box sx={{ gridColumn: { xs: '1 / -1', sm: '1 / -1' } }}>
                        <Typography variant="body2" color="text.secondary">Nameservers:</Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {result.whois.nameservers.map((ns, index) => (
                            <Chip
                              key={index}
                              label={ns}
                              size="small"
                              sx={{
                                fontFamily: 'monospace',
                                bgcolor: '#f5f5f5',
                                '& .MuiChip-label': {
                                  px: 1
                                }
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                    {result.whois.source && (
                      <Box sx={{ gridColumn: { xs: '1 / -1', sm: '1 / -1' } }}>
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'right', fontStyle: 'italic' }}>
                          Data source: {result.whois.source}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Paper>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* FAQ Section */}
      <Container maxWidth="md" sx={{ mt: 6, mb: 8 }}>
        <Paper sx={{ p: 4, borderRadius: 3, boxShadow: 2 }}>
          <Typography variant="h5" fontWeight={700} textAlign="center" mb={3}>DNS FAQ</Typography>
          {faqs.map((faq, i) => (
            <Accordion key={i} sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" fontWeight={600}>{faq.q}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary">{faq.a}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Paper>
      </Container>

      <OtherToolsSection current="DNS Super Tool" />
    </Box>
  );
};

export default DNSSuperTool; 